/// <amd-module name="Artifacts/Package/helpers/PackageDetailsHelper" />
import {
  IUpstreamOverrideModalProps,
  UpstreamOverrideModalId,
} from "Artifacts/Package/components/package-details-common/UpstreamOverrideModal";
import { PackageActions } from "Artifacts/Package/redux";
import { BadgeModalId, IBadgeModalProps } from "Artifacts/Root/components/BadgeModal";
import { DeleteModalId, IDeleteModalProps } from "Artifacts/Root/components/DeleteModal";
import { DeprecateModalId, IDeprecateModalProps } from "Artifacts/Root/components/DeprecateModal";
import { IPromoteModalProps, PromoteModalId } from "Artifacts/Root/components/PromoteModal";
import {
  IUnlistRelistModalProps,
  UnlistRelistModalId,
} from "Artifacts/Root/components/UnlistRelistModal";
import { UnyankYankModalId } from "Artifacts/Root/components/UnyankYankModal";
import { isUserFeedAdmin } from "Artifacts/Root/helpers/UserHelpers";
import { injectModal } from "Artifacts/Root/protocols/Commands";
import { filterCommandsByTag } from "Artifacts/Root/protocols/CrossProtocolTools";
import * as Tags from "Artifacts/Root/protocols/TagConstants";
import { IProtocol } from "Artifacts/Root/protocols/types";
import { ICommand } from "Artifacts/Root/protocols/types/ICommand";
import { getStore } from "Artifacts/Root/Redux";
import { IPackage, IPackageVersionIdentity } from "Artifacts/Root/types";
import { IFeed } from "Artifacts/Root/types/IFeed";
import { IVssPageContext } from "VSS/Platform/Context";

const tagFilters = new Set<string>([Tags.PackageDetails]);

export function getPromoteModalInjector(
  pageContext: IVssPageContext,
  selectedVersions: IPackageVersionIdentity[],
  feed: IFeed | undefined,
): (commands: ICommand[]) => ICommand[] {
  const store = getStore(pageContext);
  const dispatch = store.dispatch;
  const promoteModalInjector = injectModal<IPromoteModalProps>(
    pageContext,
    "promote",
    PromoteModalId,
    (command, hideModal) => ({
      packages: selectedVersions,
      onPromote: (viewId: string) => {
        return () => {
          command.action({
            feed,
            selectedVersions,
            viewId,
            onSuccessCallback: (selectedVersions: IPackageVersionIdentity[], viewName?: string) => {
              viewName && dispatch(PackageActions.fakePackagePromote(selectedVersions, viewName));
              hideModal();
            },
          });
        };
      },
      onDismiss: hideModal,
    }),
  );
  return promoteModalInjector;
}

export function getDeleteModalInjector(
  pageContext: IVssPageContext,
  selectedVersions: IPackageVersionIdentity[],
  feed: IFeed | undefined,
): (commands: ICommand[]) => ICommand[] {
  const store = getStore(pageContext);
  const dispatch = store.dispatch;
  const deleteModalInjector = injectModal<IDeleteModalProps>(
    pageContext,
    "delete",
    DeleteModalId,
    (command, hideModal) => ({
      packages: selectedVersions,
      onDelete: () => {
        command.action({
          feed,
          selectedVersions,
          onSuccessCallback: (selectedVersions: IPackageVersionIdentity[]) => {
            dispatch(PackageActions.fakePackageDelete(selectedVersions));
            hideModal();
          },
        });
      },
      onDismiss: hideModal,
    }),
  );
  return deleteModalInjector;
}

export function getUnlistRelistModalInjector(
  pageContext: IVssPageContext,
  selectedVersions: IPackageVersionIdentity[],
  feed: IFeed | undefined,
  commandId: string,
  modalId: string,
): (commands: ICommand[]) => ICommand[] {
  const store = getStore(pageContext);
  const dispatch = store.dispatch;
  const unlistRelistModalInjector = injectModal<IUnlistRelistModalProps>(
    pageContext,
    commandId,
    modalId,
    (command, hideModal) => ({
      packages: selectedVersions,
      onUnlistRelist: () => {
        command.action({
          feed,
          selectedVersions,
          onSuccessCallback: (selectedVersions: IPackageVersionIdentity[], isListed: boolean) => {
            dispatch(PackageActions.fakePackageListUnlist(selectedVersions, isListed));
            hideModal();
          },
        });
      },
      onDismiss: hideModal,
    }),
  );
  return unlistRelistModalInjector;
}

export function getDeprecateModalInjector(
  pageContext: IVssPageContext,
  selectedVersions: IPackageVersionIdentity[],
  feed: IFeed | undefined,
): (commands: ICommand[]) => ICommand[] {
  const store = getStore(pageContext);
  const dispatch = store.dispatch;
  const deprecateModalInjector = injectModal<IDeprecateModalProps>(
    pageContext,
    "deprecate",
    DeprecateModalId,
    (command, hideModal) => ({
      packages: selectedVersions,
      onDeprecate: (deprecateMessage: string) => {
        command.action({
          feed,
          selectedVersions,
          deprecateMessage,
          onSuccessCallback: (
            selectedVersions: IPackageVersionIdentity[],
            deprecateMessage: string,
          ) => {
            dispatch(PackageActions.fakePackageDeprecate(selectedVersions, deprecateMessage));
            hideModal();
          },
        });
      },
      onDismiss: hideModal,
    }),
  );
  return deprecateModalInjector;
}

export function getAllowExtVersionsModalInjector(
  pageContext: IVssPageContext,
  feed: IFeed | undefined,
  protocol: IProtocol,
  packageName: string,
  reloadUpstreamVersions?: () => void,
): (commands: ICommand[]) => ICommand[] {
  const enabled =
    protocol.getUpstreamingProperties(pageContext).isPublicUpstreamsSupported &&
    feed !== undefined &&
    isUserFeedAdmin(feed);

  const onSuccessCallback =
    reloadUpstreamVersions ||
    (() => {
      return;
    });

  const allowExtVersionsModalInjector = injectModal<IUpstreamOverrideModalProps>(
    pageContext,
    "upstream-override",
    UpstreamOverrideModalId,
    (command, hideModal) => ({
      onDismiss: hideModal,
      onOverrideToggle: (allowExternalVersions: boolean) => {
        command.action({
          feed,
          packageName,
          allowExternalVersions,
          onSuccessCallback,
        });
      },
      disabled: !enabled,
      protocol,
      packageName,
    }),
  );
  return allowExtVersionsModalInjector;
}

export function getBadgeModalInjector(
  pageContext: IVssPageContext,
  selectedVersions: IPackageVersionIdentity[],
): (commands: ICommand[]) => ICommand[] {
  const badgeModalInjector = injectModal<IBadgeModalProps>(
    pageContext,
    "package-badge",
    BadgeModalId,
    (command, hideModal) => ({
      packages: selectedVersions,
      onDismiss: hideModal,
    }),
  );
  return badgeModalInjector;
}

export function getPackageDetailsCommands(
  pageContext: IVssPageContext,
  protocol: IProtocol,
  selectedVersions: IPackageVersionIdentity[],
  feed: IFeed | undefined,
  pkg: IPackage,
): ICommand[] {
  const promoteModalInjector = getPromoteModalInjector(pageContext, selectedVersions, feed);

  const deleteModalInjector = getDeleteModalInjector(pageContext, selectedVersions, feed);

  const unlistRelistModalInjector = getUnlistRelistModalInjector(
    pageContext,
    selectedVersions,
    feed,
    "unlistRelist",
    UnlistRelistModalId,
  );

  const unyankYankModalInjector = getUnlistRelistModalInjector(
    pageContext,
    selectedVersions,
    feed,
    "unyankYank",
    UnyankYankModalId,
  );

  const allowExtVersionsModalInjector = getAllowExtVersionsModalInjector(
    pageContext,
    feed,
    protocol,
    pkg.name,
  );

  const deprecateModalInjector = getDeprecateModalInjector(pageContext, selectedVersions, feed);

  const badgeModalInjector = getBadgeModalInjector(pageContext, selectedVersions);

  const aggregateCommands = unlistRelistModalInjector(
    unyankYankModalInjector(
      deleteModalInjector(
        promoteModalInjector(
          allowExtVersionsModalInjector(
            deprecateModalInjector(
              filterCommandsByTag(
                protocol.getCommands(pageContext, selectedVersions, feed),
                tagFilters,
              ),
            ),
          ),
        ),
      ),
    ),
  );

  if (selectedVersions.length === 1) {
    return badgeModalInjector(aggregateCommands);
  }

  return aggregateCommands;
}

export function getUpstreamVersionsCommands(
  pageContext: IVssPageContext,
  protocol: IProtocol,
  feed: IFeed | undefined,
  packageName: string,
  reloadUpstreamVersions: () => void,
): ICommand[] {
  const allowExtVersionsModalInjector = getAllowExtVersionsModalInjector(
    pageContext,
    feed,
    protocol,
    packageName,
    reloadUpstreamVersions,
  );

  const aggregateCommands = allowExtVersionsModalInjector(
    filterCommandsByTag(protocol.getCommands(pageContext, [], feed), tagFilters),
  );

  return aggregateCommands;
}
